import { Input, OnInit, AfterViewInit, OnDestroy, ViewChild, Directive } from '@angular/core';
import {
  FormAttributeInput,
  FormAttributeType,
  FormAttributeConfigAction,
  FormAttributeConfig,
  getFilterByParentRule
} from '@eview/core/domain/form/form-attribute';
import { Tag, Tags } from '@eview/core/domain/tags/tag';
import { selectMap } from '@eview/core/store/selectors/map.selector';
import { selectTags } from '@eview/core/store/selectors/tags.selector';
import { selectUser } from '@eview/core/store/selectors/user.selector';
import { AppState } from '@eview/core/store/states/app.state';
import { Store, Action } from '@ngrx/store';
import { Observable, Subscription, of, BehaviorSubject, combineLatest } from 'rxjs';
import {
  map,
  debounceTime,
  take,
  filter,
  tap,
  switchMap,
  catchError,
  distinctUntilChanged
} from 'rxjs/operators';
import { MediaService } from '@eview/core/domain/media/media.service';
import { getFileNameFromMediaUrl } from '@eview/core/domain/media/media-url.helpers';
import { assertReadableUploadFile } from '@eview/core/domain/media/readable-file.validation';
import { FormAttributeEx } from './post-editor-base.component';
import {
  UserClickedMap,
  SimulateUserClickedMap,
  EMapActions
} from '@eview/core/store/actions/map.actions';
import * as moment from 'moment';
import {
  TagWithChildren,
  TagHelpers
} from '@eview/core/domain/tags/tag.helpers';
import { GoogleGeocodingService } from '@eview/features/ui/map/google-geocoding.service';
import { Actions, ofType } from '@ngrx/effects';
import { MapContext } from '@eview/core/models/map';
import { GMapComponent } from '@eview/features/ui/map/gmap.component';
import { FilterHelpers } from '@eview/core/domain/filters/filters.helper';
import { FilterItem, PostFilters, RiskFilters } from '@eview/core/domain/post/post';
import { indexOf, uniq } from 'lodash';


const MAP_ZOOM_CITY = 17; //14
const MAP_MAX_ZOOM = 24; 

@Directive()
export class FormAttributeBaseComponent
  implements OnInit, AfterViewInit, OnDestroy {
  newFilterData: any = [];
  constructor(
    public store: Store<AppState>,
    private actions: Actions,
    private googleGeocodingService: GoogleGeocodingService,
    private mediaService: MediaService
  ) {
    this.subs1 = new Subscription();
    this.parentTagIdFilter$ = new BehaviorSubject<number | null>(null);
    this.tags$ = this.store.select(selectTags);
    this.availableTags$ = this.tags$.pipe(
      map(tags => tags?.results ?? [])
    );
    this.nestedTags$ = combineLatest([
      this.tags$,
      this.parentTagIdFilter$.pipe(distinctUntilChanged())
    ]).pipe(
      map(([tags, parentTagId]) => {
        if (!this.FormAttribute) {
          return null;
        }
        const type = TagHelpers.tagTaxonomyKeyFromFormAttribute({
          input: this.FormAttribute.input,
          label: this.FormAttribute.label,
          type: this.FormAttribute.type
        });
        if (!tags || !tags.results) return null;

        const index = TagHelpers.buildTagIndex(tags);
        const filterRule = getFilterByParentRule(this.FormAttribute.config);

        // Cross-taxonomy parent filter: show only children of selected parent tag.
        if (filterRule) {
          let effectiveParentId = parentTagId;
          // Edit hydrate: parent filter may not be set yet; derive from saved child.
          if (
            !effectiveParentId &&
            this.FormAttribute.value?.[0] != null &&
            this.FormAttribute.value[0] !== -1
          ) {
            const childTag = index.byId.get(Number(this.FormAttribute.value[0]));
            effectiveParentId = TagHelpers.getTagParentId(childTag);
          }
          if (!effectiveParentId) {
            return [];
          }
          const kids = TagHelpers.childrenForParent(
            index,
            effectiveParentId,
            type
          );
          const flatRoots = kids.map(t => ({
            ...t,
            parent: null,
            parent_id: null
          }));
          return TagHelpers.ProcessForRendering(
            tags,
            { input: type, value: this.FormAttribute.value },
            index,
            flatRoots
          );
        }

        return TagHelpers.ProcessForRendering(
          tags,
          { input: type, value: this.FormAttribute.value },
          index
        );
      })
    );
  }

  public searchFailed: boolean = false;
  RiskFilters = RiskFilters;
  fileSizeError = false;
  fileTypeError = false;
  fileCorruptError = false;

  MAX_SIZE_MB = 10;


  search = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      tap(() => {
        this.searchFailed = false;
        this.isSearchingByAddress = true;
      }),
      filter(term => {
         if (term.length >= 2) {
          return true;
        } else {
          this.isSearchingByAddress = false;
          if (term.length === 0) {
            this.resetMapToDefault();
          }
          return true;
        }
      }),
      switchMap(address => {
        if (address.trim() !== '') {
          return this.googleGeocodingService.forward(address).pipe(
            map(data => {
              if (data) {
                this.searchFailed = false;
                return data.map(res => res);
              } else {
                this.searchFailed = false; // Set searchFailed to false if data is empty
                return [];
              }
            }),
            catchError(err => {
              this.searchFailed = true;
              this.isSearchingByAddress = false;
              return of([]);
            })
          );
        } else {
          // Return an empty observable if address is empty after trimming
          return of([]);
        }
      }),
      tap(() => (this.isSearchingByAddress = false))
    );

  resetMapToDefault() {
    if (this.initialPositionView) {
      // Google Maps implementation
      if (this.mapComponent && this.mapComponent.map) {
        this.mapComponent.map.setCenter({ 
          lat: this.initialPositionView.lat, 
          lng: this.initialPositionView.lon 
        });
        this.mapComponent.map.setZoom(this.initialPositionView.zoom);
      }
      if (this.value.lat && this.value.lon) {
        this.value = '';
        if (this.FormAttribute.value && this.FormAttribute.value[0] && this.FormAttribute.value[0].properties) {
          // Create a new array instead of modifying the existing one
          this.FormAttribute.value = [null];
        }
      }
      this.store.dispatch(new SimulateUserClickedMap({ actual: null }));
      this.store.dispatch(new UserClickedMap({ actual: null }));
    }
  }

  onValueSelected(result) {
    const position = result && result.item ? result.item : [];
    
    if (position && position.lat && position.lon) {
      position.zoom = MAP_ZOOM_CITY;
      
      if (this.mapComponent && this.mapComponent.map) {
        this.mapComponent.map.setCenter({ lat: position.lat, lng: position.lon });
        this.mapComponent.map.setZoom(position.zoom);
        
        setTimeout(() => {
          const latLng = new google.maps.LatLng(position.lat, position.lon);
          
          let foundFeature = null;
          this.mapComponent.map.data.forEach((feature) => {
            const geometry = feature.getGeometry();
            if (geometry && geometry.getType() === 'Polygon') {
              const paths = geometry.getArray()[0].getArray();
              const polygon = new google.maps.Polygon({ paths: paths });
              if (google.maps.geometry.poly.containsLocation(latLng, polygon)) {
                foundFeature = feature;
              }
            } else if (geometry && geometry.getType() === 'MultiPolygon') {
              geometry.getArray().forEach((poly) => {
                const paths = poly.getArray()[0].getArray();
                const polygon = new google.maps.Polygon({ paths: paths });
                if (google.maps.geometry.poly.containsLocation(latLng, polygon)) {
                  foundFeature = feature;
                }
              });
            }
          });
          
          google.maps.event.trigger(this.mapComponent.map.data, 'click', {
            latLng: latLng,
            feature: foundFeature
          });
          
          setTimeout(() => {
            google.maps.event.trigger(this.mapComponent.map, 'click', {
              latLng: latLng
            });
          }, 50);
        }, 300);
      }
    }
  }

  ngOnInit() {
    this.value = this.initValue();
    this.bindValue();
    this.handleConfig();
    this.handleTagParentFilter();

    setTimeout(() => {
        this.store.dispatch(
          new AttributeChanged({
            key: this.FormAttribute.key,
            value: this.FormAttribute.value
          })
        );
    });

    this.subs1.add(
      this.store.select(selectMap).subscribe(map => {
        if (!map || !map.actual) return;
        this.initialPositionView = map.defaults;
        // this.onValueChange();
      })
    );
  }

  ngAfterViewInit() {
    if (this.FormAttribute.type === FormAttributeType.Point) {
      const searchByAddress: Element = document.getElementById(
        'search-by-address'
      );

      this.subs1.add(
        this.actions
          .pipe(ofType<UserClickedMap>(EMapActions.UserClickedMap))
          .subscribe(action => {
            if (!(action && action.payload.actual)) {
              if ((searchByAddress as HTMLInputElement).value.length === 0) {
               (searchByAddress as HTMLInputElement).value = '';
             }
            }
          })
      );
    }
  }

  ngOnDestroy() {
    this.subs1.unsubscribe();
  }

  @Input() FormAttribute: FormAttributeEx;
  /** True when editing an existing report (do not wipe values for hidden conditional fields). */
  @Input() isPostEditMode = false;

  FormAttributeInput = FormAttributeInput;
  FormAttributeType = FormAttributeType;
  tags: Tag[] = [];

  tags$: Observable<Tags>;
  availableTags$: Observable<Tag[]>;
  nestedTags$: Observable<TagWithChildren[]>;
  /** Selected parent tag id when this field has filter_by_parent config. */
  parentTagIdFilter$: BehaviorSubject<number | null>;

  value: any;
  selectedValues = [];
  size: 'small-field' | 'medium-field' | 'large-field' | 'extra-large-field';
  initialPositionView: MapContext;

  @Input() isSMS: boolean;

  mapRegions: any = [];

  optionSelected = '';

  filters: FilterItem[] = [];

  @ViewChild(GMapComponent, { static: false }) mapComponent;

  isSearchingByAddress: boolean = false;
  noResultsFromAddress: boolean;

  subs1: Subscription;

  selectedRegion = '';
  selectedPostFilters: PostFilters[] = [];
  selectedConfig: any = [];

  /** Last seen values of fields that drive conditional rules (detect parent change). */
  private lastDriverValues: Record<string, unknown> = {};

  show: boolean = true;

  /** True after the user sets time on date+time fields (not the default 00:00). */
  protected dateTimeTimeEntered = false;

  /** Bound to native datetime-local when input is date+time. */
  datetimeLocalValue = '';

  /** Bound to native date input when input is date-only. */
  dateOnlyValue = '';

  /** Earliest allowed incident/report date (aligned with summary filters). */
  static readonly MIN_DATE_YEAR = 2018;
  static readonly MIN_DATE_MONTH = 1;
  static readonly MIN_DATE_DAY = 1;

  /** Calendar upper bound: today (local). */
  get maxDate(): { year: number; month: number; day: number } {
    const now = new Date();
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate()
    };
  }

  get minDate(): { year: number; month: number; day: number } {
    return {
      year: FormAttributeBaseComponent.MIN_DATE_YEAR,
      month: FormAttributeBaseComponent.MIN_DATE_MONTH,
      day: FormAttributeBaseComponent.MIN_DATE_DAY
    };
  }

  get maxDateTimeLocal(): string {
    return FormAttributeBaseComponent.formatLocalDateTime(new Date());
  }

  get minDateTimeLocal(): string {
    return `${FormAttributeBaseComponent.MIN_DATE_YEAR}-01-01T00:00`;
  }

  get maxDateOnly(): string {
    return FormAttributeBaseComponent.formatLocalDate(new Date());
  }

  get minDateOnly(): string {
    return `${FormAttributeBaseComponent.MIN_DATE_YEAR}-01-01`;
  }

  /** Local wall-clock YYYY-MM-DDTHH:mm (never UTC). */
  private static formatLocalDateTime(d: Date): string {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
      d.getHours()
    )}:${p(d.getMinutes())}`;
  }

  private static formatLocalDate(d: Date): string {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  }

  protected requiresDateTimeTime(): boolean {
    return (
      this.FormAttribute?.type === FormAttributeType.DateTime &&
      this.FormAttribute?.input === FormAttributeInput.DateTime
    );
  }

  protected isDateTimeTimeComplete(time: {
    hour?: number;
    minute?: number;
    second?: number;
  } | null): boolean {
    if (!time) {
      return false;
    }
    return (
      time.hour != null &&
      time.minute != null &&
      !Number.isNaN(time.hour) &&
      !Number.isNaN(time.minute)
    );
  }

  protected parseNgbDateParts(
    date: unknown
  ): { year: number; month: number; day: number } | null {
    if (!date) {
      return null;
    }
    if (typeof date === 'object' && date !== null && 'year' in date) {
      const d = date as { year: number; month: number; day: number };
      if (d.year != null && d.month != null && d.day != null) {
        return { year: d.year, month: d.month, day: d.day };
      }
    }
    if (typeof date === 'string') {
      const m = date.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (m) {
        return {
          day: parseInt(m[1], 10),
          month: parseInt(m[2], 10),
          year: parseInt(m[3], 10)
        };
      }
      const iso = date.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (iso) {
        return {
          year: parseInt(iso[1], 10),
          month: parseInt(iso[2], 10),
          day: parseInt(iso[3], 10)
        };
      }
    }
    return null;
  }

  protected syncValueFromDateTimeLocal(): void {
    if (!this.datetimeLocalValue) {
      this.value = this.requiresDateTimeTime()
        ? { date: null, time: null }
        : { date: null };
      this.dateTimeTimeEntered = false;
      return;
    }
    const m = this.parseDateTimeLocal(this.datetimeLocalValue);
    if (!m) {
      return;
    }
    this.value = {
      date: {
        year: m.year(),
        month: m.month() + 1,
        day: m.date()
      },
      time: {
        hour: m.hour(),
        minute: m.minute(),
        second: 0
      }
    };
    this.dateTimeTimeEntered = true;
  }

  protected syncValueFromDateOnly(): void {
    if (!this.dateOnlyValue) {
      this.value = { date: null };
      return;
    }
    const m = moment(this.dateOnlyValue, 'YYYY-MM-DD', true);
    if (!m.isValid()) {
      return;
    }
    this.value = {
      date: {
        year: m.year(),
        month: m.month() + 1,
        day: m.date()
      }
    };
  }

  /**
   * Parse datetime-local value as explicit local date parts (avoids moment UTC/ISO quirks).
   */
  protected parseLocalDateTimeMs(value: string): number | null {
    if (!value) {
      return null;
    }
    const m = value
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!m) {
      return null;
    }
    const year = parseInt(m[1], 10);
    const month = parseInt(m[2], 10) - 1;
    const day = parseInt(m[3], 10);
    const hour = parseInt(m[4], 10);
    const minute = parseInt(m[5], 10);
    const second = m[6] != null ? parseInt(m[6], 10) : 0;
    const dt = new Date(year, month, day, hour, minute, second, 0);
    if (
      dt.getFullYear() !== year ||
      dt.getMonth() !== month ||
      dt.getDate() !== day ||
      dt.getHours() !== hour ||
      dt.getMinutes() !== minute
    ) {
      return null;
    }
    return dt.getTime();
  }

  protected parseDateTimeLocal(value: string): moment.Moment | null {
    const ms = this.parseLocalDateTimeMs(value);
    if (ms == null) {
      return null;
    }
    return moment(ms);
  }

  protected isDateTimeInFuture(): boolean {
    if (this.requiresDateTimeTime()) {
      const selected = this.normalizeDateTimeLocalString(this.datetimeLocalValue);
      if (!selected) {
        return false;
      }
      // Lexicographic compare of local wall-clock strings — no TZ/epoch conversion.
      const nowStr = FormAttributeBaseComponent.formatLocalDateTime(new Date());
      return selected > nowStr;
    }
    if (this.dateOnlyValue) {
      const m = this.dateOnlyValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) {
        return false;
      }
      const selected = new Date(
        parseInt(m[1], 10),
        parseInt(m[2], 10) - 1,
        parseInt(m[3], 10)
      );
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selected.getTime() > today.getTime();
    }
    const parts = this.parseNgbDateParts(this.value?.date);
    if (!parts) {
      return false;
    }
    const now = new Date();
    const dateOnly = new Date(parts.year, parts.month - 1, parts.day);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return dateOnly.getTime() > today.getTime();
  }

  /** Normalize datetime-local / wall-clock ISO to `YYYY-MM-DDTHH:mm`. */
  protected normalizeDateTimeLocalString(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    const m = String(value)
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
    if (!m) {
      return null;
    }
    return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}`;
  }

  /**
   * Extract wall-clock `YYYY-MM-DDTHH:mm` from API/DB/ISO strings.
   * Digits are taken as-is (no timezone conversion).
   */
  protected extractWallClockDateTime(raw: unknown): string | null {
    return this.normalizeDateTimeLocalString(
      raw == null ? null : String(raw)
    );
  }

  /** Persist format expected by post_datetime (naive local wall-clock). */
  protected toMysqlDateTime(wallClockLocal: string): string | null {
    const wall = this.normalizeDateTimeLocalString(wallClockLocal);
    if (!wall) {
      return null;
    }
    return `${wall.replace('T', ' ')}:00`;
  }

  protected toMysqlDate(dateOnly: string): string | null {
    const m = String(dateOnly || '')
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) {
      return null;
    }
    return `${m[1]}-${m[2]}-${m[3]} 00:00:00`;
  }

  protected isDateTimeBeforeMin(): boolean {
    if (this.requiresDateTimeTime()) {
      const selected = this.normalizeDateTimeLocalString(this.datetimeLocalValue);
      if (!selected) {
        return false;
      }
      return selected < `${FormAttributeBaseComponent.MIN_DATE_YEAR}-01-01T00:00`;
    }
    if (this.dateOnlyValue) {
      const m = this.dateOnlyValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) {
        return false;
      }
      const selected = new Date(
        parseInt(m[1], 10),
        parseInt(m[2], 10) - 1,
        parseInt(m[3], 10)
      );
      const minMs = new Date(
        FormAttributeBaseComponent.MIN_DATE_YEAR,
        FormAttributeBaseComponent.MIN_DATE_MONTH - 1,
        FormAttributeBaseComponent.MIN_DATE_DAY,
        0,
        0,
        0,
        0
      ).getTime();
      return selected.getTime() < minMs;
    }
    const parts = this.parseNgbDateParts(this.value?.date);
    if (!parts) {
      return false;
    }
    const dateOnly = new Date(parts.year, parts.month - 1, parts.day);
    const minMs = new Date(
      FormAttributeBaseComponent.MIN_DATE_YEAR,
      FormAttributeBaseComponent.MIN_DATE_MONTH - 1,
      FormAttributeBaseComponent.MIN_DATE_DAY,
      0,
      0,
      0,
      0
    ).getTime();
    return dateOnly.getTime() < minMs;
  }

  protected isDateTimeFormatInvalid(): boolean {
    if (this.requiresDateTimeTime()) {
      if (!this.datetimeLocalValue) {
        return false;
      }
      return this.parseLocalDateTimeMs(this.datetimeLocalValue) == null;
    }
    if (!this.dateOnlyValue) {
      return false;
    }
    return !/^\d{4}-\d{2}-\d{2}$/.test(this.dateOnlyValue);
  }

  /** True when the typed/selected datetime cannot be submitted. */
  protected isDateTimeClientInvalid(): boolean {
    if (this.requiresDateTimeTime()) {
      if (!this.datetimeLocalValue) {
        return false;
      }
      return (
        this.isDateTimeFormatInvalid() ||
        this.isDateTimeInFuture() ||
        this.isDateTimeBeforeMin()
      );
    }
    if (!this.dateOnlyValue) {
      return false;
    }
    return (
      this.isDateTimeFormatInvalid() ||
      this.isDateTimeInFuture() ||
      this.isDateTimeBeforeMin()
    );
  }

  protected applyDateTimeClientValidity(): void {
    if (!this.FormAttribute) {
      return;
    }
    this.FormAttribute.clientInvalid = this.isDateTimeClientInvalid();
  }

  /**
   * Sync UI → model when the datetime-local string is parseable.
   * Out-of-range (future / before min) stays in the model but sets clientInvalid
   * so Submit stays disabled without wiping a valid-looking input.
   */
  protected syncDateTimeFromInputs(): void {
    if (this.requiresDateTimeTime()) {
      if (!this.datetimeLocalValue || this.isDateTimeFormatInvalid()) {
        this.value = { date: null, time: null };
        this.dateTimeTimeEntered = false;
        this.applyDateTimeClientValidity();
        return;
      }
      this.syncValueFromDateTimeLocal();
      this.applyDateTimeClientValidity();
      return;
    }

    if (!this.dateOnlyValue || this.isDateTimeFormatInvalid()) {
      this.value = { date: null };
      this.applyDateTimeClientValidity();
      return;
    }
    this.syncValueFromDateOnly();
    this.applyDateTimeClientValidity();
  }

  /** @deprecated Prefer syncDateTimeFromInputs — kept for callers that still clamp. */
  protected clampDateTimeToNow(): void {
    this.syncDateTimeFromInputs();
  }

  onDateTimeLocalChange(): void {
    this.syncDateTimeFromInputs();
    this.onValueChange();
  }

  onDateOnlyChange(): void {
    this.syncDateTimeFromInputs();
    this.onValueChange();
  }

  /**
   * Allow picker / navigation keys, but block free typing of absurd years
   * (datetime-local + readonly often disables the native picker).
   */
  blockDateTimeTyping(event: KeyboardEvent): void {
    const allowed = [
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'Backspace',
      'Delete'
    ];
    if (allowed.includes(event.key) || event.ctrlKey || event.metaKey) {
      return;
    }
    event.preventDefault();
  }

  onDateTimeChange(): void {
    this.syncDateTimeFromInputs();
    if (this.requiresDateTimeTime() && !this.value?.date) {
      if (this.value) {
        this.value.time = null;
      }
      this.dateTimeTimeEntered = false;
    }
    this.onValueChange();
  }

  onDateTimeTimeChange(): void {
    this.syncDateTimeFromInputs();
    this.dateTimeTimeEntered = true;
    this.onValueChange();
  }

  private initValue(): any {
    switch (this.FormAttribute.type) {
      case FormAttributeType.Point: {
        return { lat: null, lon: null };
      }

      case FormAttributeType.DateTime: {
        return this.requiresDateTimeTime()
          ? { date: null, time: null }
          : { date: null };
      }
      case FormAttributeType.Varchar: {
        return (this.FormAttribute.input === FormAttributeInput.Select) ? -1 : null;
      }
    }
    return null;
  }

  private bindValue(): any {
    if (this.FormAttribute.value) {
      this.value = this.FormAttribute.value;

      if (this.FormAttribute.type === FormAttributeType.Tags) {
        let matchKey = TagHelpers.tagTaxonomyKeyFromFormAttribute({
          input: this.FormAttribute.input,
          label: this.FormAttribute.label,
          type: this.FormAttribute.type
        });
        this.selectedValues[matchKey] = (this.value) ? this.value[0] : -1;
      }

      if (this.FormAttribute.input === FormAttributeInput.Checkbox) {
        this.selectedValues = this.FormAttribute.options.map(o => {
          return [...this.value].includes(o);
        });
      }

      if (
        this.FormAttribute.input === FormAttributeInput.Radio ||
        this.FormAttribute.input === FormAttributeInput.Select ||
        this.FormAttribute.input === FormAttributeInput.Number ||
        (this.FormAttribute.type === FormAttributeType.Varchar && (this.FormAttribute.input === FormAttributeInput.Text || this.FormAttribute.input === FormAttributeInput.Video))
      )
        this.value = this.value[0];
    }

    if (this.FormAttribute.type === FormAttributeType.Point) {
      if (this.FormAttribute.value) {
        this.store.dispatch(
          new UserClickedMap({
            actual: { ...this.value[0] }
          })
        );
      }
      this.subs1.add(
        this.store.select(selectMap).subscribe(map => {
          if (!map || !map.actual) return;
          this.value = map.actual;
          this.onValueChange();
        })
      );
    }

    if (this.FormAttribute.type == FormAttributeType.DateTime) {
      const raw = Array.isArray(this.FormAttribute.value)
        ? this.FormAttribute.value[0]
        : this.FormAttribute.value;
      if (raw) {
        // Digits-only: never shift via local/UTC Date conversion.
        const wall = this.extractWallClockDateTime(raw);
        if (wall) {
          const parts = wall.match(
            /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/
          );
          if (parts) {
            const year = parseInt(parts[1], 10);
            const month = parseInt(parts[2], 10);
            const day = parseInt(parts[3], 10);
            const hour = parseInt(parts[4], 10);
            const minute = parseInt(parts[5], 10);
            if (this.requiresDateTimeTime()) {
              this.datetimeLocalValue = wall;
              this.value = {
                date: { year, month, day },
                time: { hour, minute, second: 0 }
              };
              this.dateTimeTimeEntered = true;
            } else {
              const dateOnly = `${parts[1]}-${parts[2]}-${parts[3]}`;
              this.dateOnlyValue = dateOnly;
              this.value = {
                date: { year, month, day }
              };
            }
          }
        }
      }
    }

    if (
      this.FormAttribute.type == FormAttributeType.Media &&
      this.value &&
      this.value.length > 0
    ) {
      // Solo cargar media si el usuario está logeado
      this.store.select(selectUser).pipe(take(1)).subscribe(user => {
        if (user) {
          // Handle multiple files (cardinality > 1)
          if (this.FormAttribute.cardinality > 1 && Array.isArray(this.value)) {
            // this.value is an array of media IDs
            const mediaIds = this.value.filter(id => id !== null && id !== undefined);
            if (mediaIds.length > 0) {
              // Create a temporary array to hold loaded files
              const loadedFiles = [];
              let loadedCount = 0;
              
              // Load each media file
              mediaIds.forEach((mediaId, index) => {
                this.mediaService
                  .read(mediaId)
                  .pipe(take(1))
                  .subscribe(res => {
                    // Extract filename from URL if caption is not available
                    let fileName = res.caption;
                    if (!fileName && res.original_file_url) {
                      fileName = getFileNameFromMediaUrl(res.original_file_url);
                    }
                    if (!fileName) {
                      fileName = `Archivo ${index + 1}`;
                    }
                    
                    loadedFiles[index] = {
                      name: fileName,
                      id: mediaId,
                      isExisting: true
                    };
                    
                    loadedCount++;
                    
                    // When all files are loaded, update this.value
                    if (loadedCount === mediaIds.length) {
                      this.value = loadedFiles;
                    }
                  });
              });
            }
          } else {
            // Handle single file
            const mediaId = Array.isArray(this.value) ? this.value[0] : this.value;
            this.mediaService
              .read(mediaId)
              .pipe(take(1))
              .subscribe(res => {
                // Extract filename from URL if caption is not available
                let fileName = res.caption;
                if (!fileName && res.original_file_url) {
                  fileName = getFileNameFromMediaUrl(res.original_file_url);
                }
                if (!fileName) {
                  fileName = 'Archivo';
                }
                
                // Convert array value to object with name property
                this.value = {
                  name: fileName,
                  id: mediaId,
                  isExisting: true
                };
              });
          }
        }
      });
    }
    this.setExpectedDimension()
  }

  setExpectedDimension() {
    switch (this.FormAttribute.type) {
      case FormAttributeType.Varchar:
        this.size = "medium-field";
        break
      case FormAttributeType.Title:
      case FormAttributeType.Description:
      case FormAttributeType.Text:
        this.size = "large-field"
        break
      case FormAttributeType.Tags:
        this.size = "medium-field"
        break
      case FormAttributeType.Point:
        this.size = "extra-large-field"
        break
      case FormAttributeType.Decimal:
      case FormAttributeType.Int:
        this.size = "small-field"
        break
      case FormAttributeType.DateTime:
        this.size = "medium-field"
        break
      case FormAttributeType.Media:
        this.size = "medium-field"
        break
      default: this.size = "small-field"
    }
  }

  isValidDate(dateObject){
    return new Date(dateObject).toString() !== 'Invalid Date';
  }

  onValueChange() {
    this.subs1.add(this.store.select(selectMap).subscribe(map => {
      if (map) {
        this.mapRegions = map.regions;
        this.filters = FilterHelpers.GetFilters(this.mapRegions);
      }
    })
    );

    const getTypePerCase = (formAttribute): FormAttributeType => {
      if (
        formAttribute.type === FormAttributeType.Varchar &&
        formAttribute.input === FormAttributeInput.Checkbox
      ) {
        return FormAttributeType.Checkbox;
      } else {
        return formAttribute.type;
      }
    };


    switch (getTypePerCase(this.FormAttribute)) {
      case FormAttributeType.Tags:
        let matchKey = TagHelpers.tagTaxonomyKeyFromFormAttribute({
          input: this.FormAttribute.input,
          label: this.FormAttribute.label,
          type: this.FormAttribute.type
        });
        this.FormAttribute.value = (this.selectedValues[matchKey]) ? [this.selectedValues[matchKey]] : [null];
        break;

      case FormAttributeType.Title:
      case FormAttributeType.Description:
      case FormAttributeType.Content:
        {
          this.FormAttribute.value = this.value;
        }
        break;

      case FormAttributeType.DateTime:
        {
            this.syncDateTimeFromInputs();
            if (!(this.value && this.value.date)) {
              this.FormAttribute.value = [];
            } else if (
              this.requiresDateTimeTime() &&
              (!this.dateTimeTimeEntered ||
                !this.isDateTimeTimeComplete(this.value.time))
            ) {
              this.FormAttribute.value = [];
            } else if (this.requiresDateTimeTime()) {
              // Naive wall-clock only — never Date/toISOString/timezone math.
              const mysql = this.toMysqlDateTime(this.datetimeLocalValue);
              this.FormAttribute.value = mysql ? [mysql] : [];
            } else {
              const mysql = this.toMysqlDate(this.dateOnlyValue);
              this.FormAttribute.value = mysql ? [mysql] : [];
            }
        }
        break;

      case FormAttributeType.Checkbox:
        {
          this.FormAttribute.value = this.selectedValues
            .map((v, i) => {
              if (!v) return;
              return this.FormAttribute.options[i];
            })
            .filter(i => i);
        }
        break;

      case FormAttributeType.Media:
        {
          this.FormAttribute.value = this.value;
        }
      break;

      default:
        this.FormAttribute.value = this.value ? [this.value] : null;
        if (this.FormAttribute.label === RiskFilters.PROVINCE) {
          globals.selectedRegion = this.value;
          if (globals.selectedRegion !== '-1') {
            let temp = [], districts = [];
            this.mapRegions[globals.selectedRegion].forEach(element => {
              if (indexOf(temp, element.district) === -1) {
                temp.push(element.district);
                districts.push({ name: element.district, value: element.district });
              }
            });
            this.newFilterData = [];
            this.setFilterValues(PostFilters.District, districts);
          }
        }

        if (this.FormAttribute.label === RiskFilters.DISTRICT) {
          globals.selectedZone = this.value;
          if (globals.selectedRegion !== undefined) {
            const province = this.mapRegions[globals.selectedRegion];
            if (province !== '-1') {
              const districts = province.filter(item => (item.district === this.value));
            }
          }
        }
    }

    if (
      this.FormAttribute.type === FormAttributeType.DateTime ||
      (this.FormAttribute.value &&
        this.FormAttribute.value[0] != -1 &&
        this.FormAttribute.value.length > 0)
    ) {
      this.store.dispatch(
        new AttributeChanged({
          key: this.FormAttribute.key,
          value: this.FormAttribute.value
        })
      );
    }
  }

  setFilterValues(keyToBeSet: string, values: string[]) {
    this.newFilterData = [];
    this.filters.map((item) => {
      if (item.queryParam === keyToBeSet) {
        item.values = values;
      }
      this.newFilterData.push(item);
    });
  }

  onDeleteConfirmationClick() {
    this.value = null;
    this.onValueChange();
  }

  async onMediaSelect(event: { target: { files: any } }) {
    const input = event.target as HTMLInputElement;
    this.fileSizeError = false;
    this.fileTypeError = false;
    this.fileCorruptError = false;
    if (!input?.files?.length) {
      return;
    }
    const file = input.files[0];
    const result = await assertReadableUploadFile(file, {
      kind: 'pdf-or-image',
      maxMb: this.MAX_SIZE_MB
    });
    if (result.ok === false) {
      if (result.errorKey === 'FILE_SIZE_EXCEEDED') {
        this.value = '';
        this.fileSizeError = true;
      } else if (result.errorKey === 'FILE_CORRUPT_OR_UNREADABLE') {
        this.fileCorruptError = true;
      } else {
        this.fileTypeError = true;
      }
      input.value = '';
      return;
    }
    this.value = file;
    this.onValueChange();
  }

  private handleConfig() {
    if (
      !this.FormAttribute.config ||
      (this.FormAttribute.config && this.FormAttribute.config.length === 0)
    )
      return;

    // Conditional "show" fields start hidden until a driver value matches.
    const hasShowRule = (this.FormAttribute.config || []).some(
      c =>
        c?.action === FormAttributeConfigAction.Show &&
        c?.target?.key != null &&
        c?.target?.key !== ''
    );
    if (hasShowRule) {
      this.show = false;
    }

    this.subs1.add(
      this.actions
        .pipe(
          ofType<AttributeChanged>(EFormAttributeActions.AttributeChanged),
          filter(action => action.payload.key !== this.FormAttribute.key)
        )
        .subscribe(action => {
          const driverKeys = uniq(
            (this.FormAttribute.config || []).map(c => c.target?.key).filter(Boolean)
          );
          if (driverKeys.includes(action.payload.key)) {
            const nextVal = Array.isArray(action.payload.value)
              ? action.payload.value[0]
              : action.payload.value;
            const prevVal = this.lastDriverValues[action.payload.key];
            if (prevVal !== undefined && prevVal != nextVal) {
              // Hydration on edit: driver goes empty → saved value. Do not wipe children.
              const prevEmpty =
                prevVal == null || prevVal === '' || prevVal === -1 || prevVal === '-1';
              const nextEmpty =
                nextVal == null || nextVal === '' || nextVal === -1 || nextVal === '-1';
              if (!(this.isPostEditMode && prevEmpty && !nextEmpty)) {
                this.clearConditionalFieldValue();
              }
            }
            this.lastDriverValues[action.payload.key] = nextVal;
          }

          const evaluate = (
            config: FormAttributeConfig[],
            action: FormAttributeConfigAction,
            attributeChanged: { key: string; value: any }
          ): boolean => {
            switch (action) {
              case FormAttributeConfigAction.Show:
                if (!config.find(c => c.action)) return true;
            }

            const matchingConfigs = config.filter(
              c =>
                c.action === action &&
                c.target?.key != null &&
                c.target.key === attributeChanged.key
            );

            if (matchingConfigs && matchingConfigs.length > 0) {
              const matchingValues = matchingConfigs.map(c => c.target?.value);

              // Avoid modifying read-only properties directly
              let attributeValue = Array.isArray(attributeChanged.value)
                ? [...attributeChanged.value]  // Create a copy if it's an array
                : [attributeChanged.value];    // Wrap non-array value in an array

              if (attributeValue.length === 0) return false;

              return attributeValue
                .map(
                  v =>
                    matchingValues.includes(v) ||
                    matchingValues.includes('' + v)
                )
                .reduce((e, i) => (e = e || i), false);
            }
            return undefined;
          };

          let e = evaluate(
            this.FormAttribute.config,
            FormAttributeConfigAction.Show,
            action.payload
          );

          if (!(typeof e === 'undefined')) {
            if (this.selectedConfig[this.FormAttribute.key]) {
              this.selectedConfig[this.FormAttribute.key] = this.selectedConfig[this.FormAttribute.key].filter(item => item.key !== action.payload.key);
              this.selectedConfig[this.FormAttribute.key] = [...this.selectedConfig[this.FormAttribute.key], { key: action.payload.key, val: e }];
            } else {
              this.selectedConfig[this.FormAttribute.key] = [{ key: action.payload.key, val: e }];
            }

            const showCount = this.selectedConfig[this.FormAttribute.key].filter(item => item.val === true);
            this.show = (showCount.length > 0) ? true : false;

            if (!this.show) {
              // Only count show/filter rules that have a target (e.g. rows_catalog has none).
              const uniqueCount = uniq(
                this.FormAttribute.config
                  .map(val => val.target?.key)
                  .filter(Boolean)
              );
              if (uniqueCount.length === this.selectedConfig[this.FormAttribute.key].length) {
                this.clearConditionalFieldValue();
              }
            }
          }
        })
    );
  }

  // Helper method to update value safely
  private updateFormAttributeValue(newValue: any) {
    // Perform the update in a way that respects immutability
    this.value = newValue;
  }

  /** Clears value when a conditional field is hidden (e.g. parent selection changed). */
  private clearConditionalFieldValue(): void {
    if (
      this.FormAttribute.type === FormAttributeType.Varchar &&
      this.FormAttribute.input === FormAttributeInput.Select
    ) {
      this.updateFormAttributeValue(-1);
      this.FormAttribute.value = null;
    } else if (this.FormAttribute.type === FormAttributeType.Tags) {
      const matchKey = TagHelpers.tagTaxonomyKeyFromFormAttribute({
        input: this.FormAttribute.input,
        label: this.FormAttribute.label,
        type: this.FormAttribute.type
      });
      this.selectedValues[matchKey] = null;
      this.FormAttribute.value = [null];
      this.store.dispatch({
        type: '[TagSelector] Change',
        payload: { type: matchKey, id: null, val: null }
      });
    } else {
      this.updateFormAttributeValue(null);
      this.FormAttribute.value = null;
    }
    this.store.dispatch(
      new AttributeChanged({
        key: this.FormAttribute.key,
        value: this.FormAttribute.value ?? []
      })
    );
  }

  /**
   * When config has filter_by_parent, listen to the driver field and filter
   * this field's tag options by tags.parent_id (O(1) index lookup).
   * Hide the field when the parent has no children (matches legacy show cascades).
   */
  /** True after filter_by_parent has seen a real parent (or derived one on edit). */
  private parentFilterReady = false;

  protected handleTagParentFilter() {
    const rule = getFilterByParentRule(this.FormAttribute?.config);
    if (!rule?.target?.key) {
      return;
    }
    const parentKey = rule.target.key;

    // No options until a parent with children is selected
    this.show = false;

    // Edit: derive parent from saved child tag via index
    if (
      this.FormAttribute.value &&
      this.FormAttribute.value.length > 0 &&
      this.FormAttribute.value[0]
    ) {
      this.tags$
        .pipe(
          filter(tags => !!tags?.results?.length),
          take(1)
        )
        .subscribe(tags => {
          const index = TagHelpers.buildTagIndex(tags);
          const childId = Number(this.FormAttribute.value[0]);
          const childTag = index.byId.get(childId);
          const parentId = TagHelpers.getTagParentId(childTag);
          if (parentId) {
            this.parentTagIdFilter$.next(parentId);
            this.syncFilterByParentVisibility(parentId, index);
            this.parentFilterReady = true;
          }
        });
    }

    this.subs1.add(
      this.actions
        .pipe(
          ofType<AttributeChanged>(EFormAttributeActions.AttributeChanged),
          filter(action => action.payload.key === parentKey)
        )
        .subscribe(action => {
          this.resolveParentTagId(action.payload.value);
        })
    );
  }

  private resolveParentTagId(value: any) {
    if (value && value.length > 0 && value[0] !== null && value[0] !== -1) {
      const parentTagId = Number(value[0]);
      this.tags$
        .pipe(
          filter(tags => !!tags?.results?.length),
          take(1)
        )
        .subscribe(tags => {
          const index = TagHelpers.buildTagIndex(tags);
          if (index.byId.has(parentTagId)) {
            this.parentTagIdFilter$.next(parentTagId);
            this.syncFilterByParentVisibility(parentTagId, index);
            this.clearChildTagIfOutsideParent(parentTagId, index);
            this.parentFilterReady = true;
          } else {
            this.parentTagIdFilter$.next(null);
            this.syncFilterByParentVisibility(null, index);
            this.clearDependentTagSelection(this.parentFilterReady);
          }
        });
    } else {
      this.parentTagIdFilter$.next(null);
      this.syncFilterByParentVisibility(null);
      // Skip wipe during edit hydration (empty parent event before saved category loads).
      this.clearDependentTagSelection(this.parentFilterReady);
    }
  }

  /**
   * Show filter_by_parent fields only when the selected parent has at least one child
   * in this field's taxonomy (e.g. hide Subcategoría if the category has none).
   */
  private syncFilterByParentVisibility(
    parentTagId: number | null,
    index?: ReturnType<typeof TagHelpers.buildTagIndex>
  ): void {
    if (!getFilterByParentRule(this.FormAttribute?.config)) {
      return;
    }
    if (!parentTagId) {
      if (this.show) {
        this.show = false;
        this.clearDependentTagSelection(this.parentFilterReady);
      } else {
        this.show = false;
      }
      return;
    }

    const apply = (tagIndex: ReturnType<typeof TagHelpers.buildTagIndex>) => {
      const taxonomy = TagHelpers.tagTaxonomyKeyFromFormAttribute({
        input: this.FormAttribute.input,
        label: this.FormAttribute.label,
        type: this.FormAttribute.type
      });
      const kids = TagHelpers.childrenForParent(
        tagIndex,
        parentTagId,
        taxonomy
      );
      const shouldShow = !!(kids && kids.length);
      if (!shouldShow && this.show) {
        this.clearDependentTagSelection(this.parentFilterReady);
      }
      this.show = shouldShow;
    };

    if (index) {
      apply(index);
      return;
    }
    this.tags$
      .pipe(
        filter(tags => !!tags?.results?.length),
        take(1)
      )
      .subscribe(tags => apply(TagHelpers.buildTagIndex(tags)));
  }

  private clearChildTagIfOutsideParent(
    parentTagId: number,
    index: ReturnType<typeof TagHelpers.buildTagIndex>
  ): void {
    const currentId = this.FormAttribute?.value?.[0];
    if (currentId == null || currentId === -1) {
      return;
    }
    const childTag = index.byId.get(Number(currentId));
    if (TagHelpers.getTagParentId(childTag) !== parentTagId) {
      this.clearDependentTagSelection(true);
    }
  }

  private clearDependentTagSelection(force = false): void {
    // On edit, parent filter races (empty parent / other fields' SetById) must not
    // wipe a saved subcategory — only clear when the parent truly changed.
    if (
      !force &&
      this.isPostEditMode &&
      this.FormAttribute?.value?.[0] != null &&
      this.FormAttribute.value[0] !== -1 &&
      this.FormAttribute.value[0] !== ''
    ) {
      return;
    }
    this.updateFormAttributeValue(-1);
    this.FormAttribute.value = [null];
    const matchKey = TagHelpers.tagTaxonomyKeyFromFormAttribute({
      input: this.FormAttribute.input,
      label: this.FormAttribute.label,
      type: this.FormAttribute.type
    });
    this.selectedValues[matchKey] = null;
    this.store.dispatch(
      new AttributeChanged({
        key: this.FormAttribute.key,
        value: []
      })
    );
  }

}

export enum EFormAttributeActions {
  UnresolvedAddress = '[FormAttribute] Unresolved address',
  AttributeChanged = '[FormAttribute] Changed'
}

export class FormAttributeUnresolvedAddress implements Action {
  readonly type = EFormAttributeActions.UnresolvedAddress;
}

export class AttributeChanged implements Action {
  readonly type = EFormAttributeActions.AttributeChanged;
  constructor(public payload: { key: string; value: any }) { }
}

export const globals = {
  selectedRegion: '',
  selectedZone: '',
  selectedWoreda: ''
}
